'use client';

import { cn, Avatar, AvatarImage, AvatarFallback } from './_adapter';
import { ThumbsUp, MessageSquare, Repeat2, Send } from 'lucide-react';

interface LinkedInPostProps {
  authorName: string;
  authorHeadline?: string;
  authorAvatarUrl?: string;
  content: string;
  media?: React.ReactNode;
  className?: string;
}

export function LinkedInPost({
  authorName,
  authorHeadline = 'Content Creator',
  authorAvatarUrl,
  content,
  media,
  className,
}: LinkedInPostProps) {
  const initials = authorName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={cn('bg-white dark:bg-zinc-900 rounded-lg border shadow-sm max-w-lg', className)}>
      {/* Header */}
      <div className="p-4 pb-2">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12">
            {authorAvatarUrl ? (
              <AvatarImage src={authorAvatarUrl} alt={authorName} />
            ) : null}
            <AvatarFallback className="bg-blue-600 text-white text-sm font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-foreground">{authorName}</p>
            <p className="text-xs text-muted-foreground truncate">{authorHeadline}</p>
            <p className="text-xs text-muted-foreground">Just now</p>
          </div>
          <LinkedInLogo className="h-5 w-5 text-[#0A66C2] flex-shrink-0" />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-sm text-foreground whitespace-pre-wrap">{content}</p>
      </div>

      {/* Media */}
      {media && <div className="border-t border-b">{media}</div>}

      {/* Engagement Stats */}
      <div className="px-4 py-2 flex items-center justify-between text-xs text-muted-foreground border-b">
        <div className="flex items-center gap-1">
          <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600">
            <ThumbsUp className="w-2.5 h-2.5 text-white" />
          </span>
          <span>0</span>
        </div>
        <span>0 comments</span>
      </div>

      {/* Actions */}
      <div className="px-2 py-1 flex items-center justify-around">
        <ActionButton icon={ThumbsUp} label="Like" />
        <ActionButton icon={MessageSquare} label="Comment" />
        <ActionButton icon={Repeat2} label="Repost" />
        <ActionButton icon={Send} label="Send" />
      </div>
    </div>
  );
}

function ActionButton({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) {
  return (
    <button className="flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:bg-muted rounded transition-colors">
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function LinkedInLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-label="LinkedIn"
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}
